/**
 * AuthManager — Login/Register modal + Firebase Auth state management.
 * Follows the same DOM-construction pattern as FaqModal and HeroThicknessModal.
 */
export default class AuthManager {
  constructor(firebaseService) {
    this.firebase = firebaseService;
    this.user = null; // { email, name, company }
    this.observers = [];
    this.overlay = null;
    this.mode = 'login'; // 'login' | 'register'

    this.dashboard = null;
    this.dropdown = null;

    this._buildModal();
    this._buildDropdown();
    this._bindEvents();

    // Listen to Firebase auth state changes
    this.firebase.onAuthStateChanged((fbUser, profile) => {
      if (fbUser) {
        this.user = {
          email: fbUser.email,
          name: fbUser.displayName || profile?.name || '',
          company: profile?.company || ''
        };
        this._notifyObservers(true);
      } else {
        this.user = null;
        this._notifyObservers(false);
      }
      this._updateAuthButtons();
    });
  }

  // --- Public API ---

  isLoggedIn() { return !!this.user; }
  getUser() { return this.user; }

  setDashboard(dashboard) {
    this.dashboard = dashboard;
  }

  toggleDropdown() {
    if (!this.dropdown) return;
    if (this.dropdown.classList.contains('hidden')) {
      this._updateDropdownInfo();
      this.dropdown.classList.remove('hidden');
      setTimeout(() => {
        const handler = (e) => {
          if (!this.dropdown.contains(e.target) && !e.target.closest('#auth-toggle')) {
            this.dropdown.classList.add('hidden');
            document.removeEventListener('click', handler);
          }
        };
        document.addEventListener('click', handler);
      }, 0);
    } else {
      this.dropdown.classList.add('hidden');
    }
  }

  onAuthChange(callback) {
    this.observers.push(callback);
    // Fire immediately with current state
    callback(this.isLoggedIn(), this.user);
  }

  openLoginModal() {
    this.mode = 'login';
    this._updateModalView();
    this.overlay.classList.remove('hidden');
  }

  openRegisterModal() {
    this.mode = 'register';
    this._updateModalView();
    this.overlay.classList.remove('hidden');
  }

  closeModal() {
    this.overlay.classList.add('hidden');
    this._clearError();
  }

  async login(email, password) {
    this._clearError();
    this._setLoading(true);
    try {
      await this.firebase.loginWithEmail(email, password);
      // onAuthStateChanged will update this.user and notify observers
      this.closeModal();
      return true;
    } catch (e) {
      const msg = this._firebaseErrorMessage(e.code);
      this._showError(msg);
      return false;
    } finally {
      this._setLoading(false);
    }
  }

  async register(email, password, name, company) {
    this._clearError();
    this._setLoading(true);
    try {
      await this.firebase.registerWithEmail(email, password, name, company);
      // onAuthStateChanged will update this.user and notify observers
      this.closeModal();
      return true;
    } catch (e) {
      const msg = this._firebaseErrorMessage(e.code);
      this._showError(msg);
      return false;
    } finally {
      this._setLoading(false);
    }
  }

  async logout() {
    await this.firebase.logout();
    // onAuthStateChanged will clear this.user and notify observers
  }

  // --- Private ---

  _firebaseErrorMessage(code) {
    const map = {
      'auth/invalid-email': 'Ungültige E-Mail-Adresse.',
      'auth/user-disabled': 'Dieses Konto wurde deaktiviert.',
      'auth/user-not-found': 'Ungültige Anmeldedaten.',
      'auth/wrong-password': 'Ungültige Anmeldedaten.',
      'auth/invalid-credential': 'Ungültige Anmeldedaten.',
      'auth/email-already-in-use': 'Diese E-Mail ist bereits registriert.',
      'auth/weak-password': 'Passwort muss mindestens 6 Zeichen lang sein.',
      'auth/too-many-requests': 'Zu viele Versuche. Bitte warten Sie einen Moment.',
      'auth/network-request-failed': 'Verbindungsfehler. Bitte versuche es erneut.',
    };
    return map[code] || 'Anmeldung fehlgeschlagen.';
  }

  _notifyObservers(isLoggedIn) {
    this.observers.forEach(cb => cb(isLoggedIn, this.user));
  }

  _buildDropdown() {
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'auth-dropdown hidden';
    this.dropdown.id = 'auth-dropdown';

    this.dropdown.innerHTML = `
      <div class="auth-dropdown-header">
        <div class="auth-dropdown-avatar" id="dropdown-avatar"></div>
        <div class="auth-dropdown-info">
          <div class="auth-dropdown-name" id="dropdown-name"></div>
          <div class="auth-dropdown-email" id="dropdown-email"></div>
        </div>
      </div>
      <div class="auth-dropdown-divider"></div>
      <button class="auth-dropdown-item" id="dropdown-dashboard">
        <i class="fas fa-th-large"></i>
        <span>Mein Dashboard</span>
      </button>
      <button class="auth-dropdown-item" id="dropdown-logout">
        <i class="fas fa-sign-out-alt"></i>
        <span>Abmelden</span>
      </button>
    `;

    const authToggle = document.getElementById('auth-toggle');
    if (authToggle && authToggle.parentElement) {
      authToggle.parentElement.style.position = 'relative';
      authToggle.parentElement.appendChild(this.dropdown);
    } else {
      document.body.appendChild(this.dropdown);
    }

    this.dropdown.querySelector('#dropdown-dashboard').addEventListener('click', () => {
      this.dropdown.classList.add('hidden');
      if (this.dashboard) this.dashboard.show();
    });

    this.dropdown.querySelector('#dropdown-logout').addEventListener('click', () => {
      this.dropdown.classList.add('hidden');
      this.logout();
    });
  }

  _updateDropdownInfo() {
    if (!this.user) return;
    const avatar = this.dropdown.querySelector('#dropdown-avatar');
    const name = this.dropdown.querySelector('#dropdown-name');
    const email = this.dropdown.querySelector('#dropdown-email');
    if (avatar) avatar.textContent = this._getInitials(this.user.name || this.user.email);
    if (name) name.textContent = this.user.name || this.user.email;
    if (email) email.textContent = this.user.email;
  }

  _buildModal() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'auth-overlay hidden';
    this.overlay.id = 'auth-overlay';

    const modal = document.createElement('div');
    modal.className = 'auth-modal';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'auth-close';
    closeBtn.innerHTML = '<i class="fas fa-times"></i>';
    closeBtn.addEventListener('click', () => this.closeModal());

    const logo = document.createElement('img');
    logo.className = 'auth-logo';
    logo.alt = 'CDR';
    const existingLogo = document.querySelector('.cdr-logo');
    logo.src = existingLogo?.src || 'cdr-logo.0fc37c88.svg';

    this.titleEl = document.createElement('h2');
    this.titleEl.className = 'auth-title';

    this.errorEl = document.createElement('div');
    this.errorEl.className = 'auth-error';

    this.loginForm = this._buildLoginForm();
    this.registerForm = this._buildRegisterForm();
    this.userView = this._buildUserView();

    modal.appendChild(closeBtn);
    modal.appendChild(logo);
    modal.appendChild(this.titleEl);
    modal.appendChild(this.errorEl);
    modal.appendChild(this.loginForm);
    modal.appendChild(this.registerForm);
    modal.appendChild(this.userView);

    this.overlay.appendChild(modal);

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.closeModal();
    });

    document.body.appendChild(this.overlay);
  }

  _buildLoginForm() {
    const form = document.createElement('div');
    form.className = 'auth-form';
    form.id = 'auth-login-form';

    form.innerHTML = `
      <div class="auth-form-group">
        <label class="auth-form-label">E-Mail</label>
        <div class="auth-input-wrap">
          <i class="fas fa-envelope auth-input-icon"></i>
          <input class="auth-form-input" type="email" id="auth-login-email" placeholder="name@firma.ch" autocomplete="email">
        </div>
      </div>
      <div class="auth-form-group">
        <label class="auth-form-label">Passwort</label>
        <div class="auth-input-wrap">
          <i class="fas fa-lock auth-input-icon"></i>
          <input class="auth-form-input" type="password" id="auth-login-password" placeholder="Passwort" autocomplete="current-password">
          <button type="button" class="auth-pw-toggle" data-target="auth-login-password" title="Passwort anzeigen">
            <i class="fas fa-eye-slash"></i>
          </button>
        </div>
      </div>
      <button class="auth-submit" id="auth-login-btn">
        <span class="auth-submit-text">Anmelden</span>
        <span class="auth-submit-spinner hidden"><i class="fas fa-circle-notch fa-spin"></i></span>
      </button>
      <div class="auth-switch">
        Noch kein Konto? <a href="#" id="auth-to-register">Registrieren</a>
      </div>
      <a class="auth-forgot" href="#" id="auth-forgot-pw">Passwort vergessen?</a>
    `;

    return form;
  }

  _buildRegisterForm() {
    const form = document.createElement('div');
    form.className = 'auth-form hidden';
    form.id = 'auth-register-form';

    form.innerHTML = `
      <div class="auth-form-group">
        <label class="auth-form-label">Name</label>
        <div class="auth-input-wrap">
          <i class="fas fa-user auth-input-icon"></i>
          <input class="auth-form-input" type="text" id="auth-reg-name" placeholder="Vor- und Nachname" autocomplete="name">
        </div>
      </div>
      <div class="auth-form-group">
        <label class="auth-form-label">Firma</label>
        <div class="auth-input-wrap">
          <i class="fas fa-building auth-input-icon"></i>
          <input class="auth-form-input" type="text" id="auth-reg-company" placeholder="Firma" autocomplete="organization">
        </div>
      </div>
      <div class="auth-form-group">
        <label class="auth-form-label">E-Mail</label>
        <div class="auth-input-wrap">
          <i class="fas fa-envelope auth-input-icon"></i>
          <input class="auth-form-input" type="email" id="auth-reg-email" placeholder="name@firma.ch" autocomplete="email">
        </div>
      </div>
      <div class="auth-form-group">
        <label class="auth-form-label">Passwort</label>
        <div class="auth-input-wrap">
          <i class="fas fa-lock auth-input-icon"></i>
          <input class="auth-form-input" type="password" id="auth-reg-password" placeholder="Mind. 6 Zeichen" autocomplete="new-password">
          <button type="button" class="auth-pw-toggle" data-target="auth-reg-password" title="Passwort anzeigen">
            <i class="fas fa-eye-slash"></i>
          </button>
        </div>
      </div>
      <button class="auth-submit" id="auth-register-btn">
        <span class="auth-submit-text">Registrieren</span>
        <span class="auth-submit-spinner hidden"><i class="fas fa-circle-notch fa-spin"></i></span>
      </button>
      <div class="auth-switch">
        Bereits ein Konto? <a href="#" id="auth-to-login">Anmelden</a>
      </div>
    `;

    return form;
  }

  _buildUserView() {
    const view = document.createElement('div');
    view.className = 'auth-user-view hidden';
    view.id = 'auth-user-view';

    view.innerHTML = `
      <div class="auth-user-info">
        <div class="auth-user-avatar" id="auth-user-avatar"></div>
        <div class="auth-user-details">
          <div class="auth-user-name" id="auth-user-name"></div>
          <div class="auth-user-email" id="auth-user-email"></div>
          <div class="auth-user-company" id="auth-user-company"></div>
        </div>
      </div>
      <button class="auth-logout" id="auth-logout-btn">
        <i class="fas fa-sign-out-alt"></i>
        <span>Abmelden</span>
      </button>
    `;

    return view;
  }

  _bindEvents() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('#auth-login-btn')) {
        e.preventDefault();
        const email = document.getElementById('auth-login-email').value.trim();
        const password = document.getElementById('auth-login-password').value;
        if (email && password) this.login(email, password);
      }

      if (e.target.closest('#auth-register-btn')) {
        e.preventDefault();
        const name = document.getElementById('auth-reg-name').value.trim();
        const company = document.getElementById('auth-reg-company').value.trim();
        const email = document.getElementById('auth-reg-email').value.trim();
        const password = document.getElementById('auth-reg-password').value;
        if (email && password) this.register(email, password, name, company);
      }

      if (e.target.closest('#auth-to-register')) {
        e.preventDefault();
        this.mode = 'register';
        this._updateModalView();
      }

      if (e.target.closest('#auth-to-login')) {
        e.preventDefault();
        this.mode = 'login';
        this._updateModalView();
      }

      const pwToggle = e.target.closest('.auth-pw-toggle');
      if (pwToggle) {
        e.preventDefault();
        const targetId = pwToggle.dataset.target;
        const input = document.getElementById(targetId);
        if (input) {
          const isPassword = input.type === 'password';
          input.type = isPassword ? 'text' : 'password';
          const icon = pwToggle.querySelector('i');
          if (icon) {
            icon.className = isPassword ? 'fas fa-eye' : 'fas fa-eye-slash';
          }
        }
      }

      if (e.target.closest('#auth-forgot-pw')) {
        e.preventDefault();
        this._showError('Bitte kontaktieren Sie uns unter info@cdr.ch für ein Passwort-Reset.');
      }

      if (e.target.closest('#auth-logout-btn')) {
        e.preventDefault();
        this.logout();
        this.closeModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      if (this.overlay.classList.contains('hidden')) return;

      if (this.mode === 'login') {
        const email = document.getElementById('auth-login-email').value.trim();
        const password = document.getElementById('auth-login-password').value;
        if (email && password) this.login(email, password);
      } else if (this.mode === 'register') {
        const name = document.getElementById('auth-reg-name').value.trim();
        const company = document.getElementById('auth-reg-company').value.trim();
        const email = document.getElementById('auth-reg-email').value.trim();
        const password = document.getElementById('auth-reg-password').value;
        if (email && password) this.register(email, password, name, company);
      }
    });
  }

  _updateModalView() {
    this._clearError();

    if (this.isLoggedIn()) {
      this.titleEl.textContent = 'Mein Konto';
      this.loginForm.classList.add('hidden');
      this.registerForm.classList.add('hidden');
      this.userView.classList.remove('hidden');

      const avatar = document.getElementById('auth-user-avatar');
      const nameEl = document.getElementById('auth-user-name');
      const emailEl = document.getElementById('auth-user-email');
      const companyEl = document.getElementById('auth-user-company');

      if (avatar) avatar.textContent = this._getInitials(this.user.name || this.user.email);
      if (nameEl) nameEl.textContent = this.user.name || '';
      if (emailEl) emailEl.textContent = this.user.email;
      if (companyEl) companyEl.textContent = this.user.company || '';
    } else if (this.mode === 'login') {
      this.titleEl.textContent = 'Anmelden';
      this.loginForm.classList.remove('hidden');
      this.registerForm.classList.add('hidden');
      this.userView.classList.add('hidden');
    } else {
      this.titleEl.textContent = 'Konto erstellen';
      this.loginForm.classList.add('hidden');
      this.registerForm.classList.remove('hidden');
      this.userView.classList.add('hidden');
    }
  }

  _updateAuthButtons() {
    document.querySelectorAll('#auth-toggle, #mobile-auth-toggle').forEach(btn => {
      if (this.isLoggedIn()) {
        btn.innerHTML = `<span class="auth-avatar-sm">${this._getInitials(this.user.name || this.user.email)}</span>`;
        btn.title = this.user.name || this.user.email;
      } else {
        btn.innerHTML = '<i class="fas fa-user"></i>';
        btn.title = 'Anmelden';
      }
    });
  }

  _getInitials(str) {
    if (!str) return '?';
    const parts = str.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return str.substring(0, 2).toUpperCase();
  }

  _showError(msg) {
    this.errorEl.textContent = msg;
    this.errorEl.classList.add('visible');
  }

  _clearError() {
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('visible');
  }

  _setLoading(loading) {
    const btnId = this.mode === 'login' ? 'auth-login-btn' : 'auth-register-btn';
    const btn = document.getElementById(btnId);
    if (!btn) return;
    const text = btn.querySelector('.auth-submit-text');
    const spinner = btn.querySelector('.auth-submit-spinner');
    if (loading) {
      btn.disabled = true;
      if (text) text.classList.add('hidden');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      btn.disabled = false;
      if (text) text.classList.remove('hidden');
      if (spinner) spinner.classList.add('hidden');
    }
  }
}
